import React from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { closeModal } from "../redux/uireducers/modalstrip";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

interface ModalStripWrapperProps {
  $modalType: string | null;
  $isOpen: boolean;
}

const ModalStripWrapper = styled.div<ModalStripWrapperProps>`
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(${({ $isOpen }) => ($isOpen ? "0" : "-20px")});
  width: 90%;
  max-width: 460px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid
    ${({ $modalType }) =>
      $modalType === "danger"
        ? "rgba(239, 68, 68, 0.4)"
        : $modalType === "success"
        ? "rgba(34, 197, 94, 0.4)"
        : "rgba(59, 130, 246, 0.4)"};
  color: #ffffff;
  background: ${({ $modalType }) =>
    $modalType === "danger"
      ? "linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)"
      : $modalType === "success"
      ? "linear-gradient(135deg, #15803d 0%, #166534 100%)"
      : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)"};
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.5), 0 0 12px rgba(0, 0, 0, 0.2);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MessageText = styled.div`
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
  word-break: break-word;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ModalStrip: React.FC = () => {
  const dispatch = useAppDispatch();
  const { modalType, message } = useAppSelector(
    (state) => state.uielements.modalstrip
  );

  const isOpen = Boolean(modalType && message);

  const renderIcon = () => {
    switch (modalType) {
      case "danger":
        return <AlertCircle size={20} />;
      case "success":
        return <CheckCircle2 size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  return (
    <ModalStripWrapper $modalType={modalType} $isOpen={isOpen} role="alert">
      <ContentRow>
        <IconWrapper>{renderIcon()}</IconWrapper>
        <MessageText>{message}</MessageText>
      </ContentRow>
      <CloseButton
        type="button"
        aria-label="Close notification"
        onClick={() => dispatch(closeModal())}
      >
        <X size={16} />
      </CloseButton>
    </ModalStripWrapper>
  );
};

export default ModalStrip;
